import { inject, nextTick, onBeforeUnmount, onMounted } from 'vue';
import { RETAINED_MODAL_DIRTY_KEY } from './useRetainedModalContent';

export interface ModalUnsavedChangesOptions {
    /** Capture the clean baseline a tick after mount. Turn off for forms that seed asynchronously and call `markClean()` themselves. */
    auto?: boolean;
}

// Metadata alone cannot tell two `File`s apart — a replacement with the same name, size and
// `lastModified` would serialise identically and the X would drop it without asking. So each object
// is tagged once, by reference. The cost is that re-picking the same file off disk reads as a
// change; a needless "Are you sure?" is the safe side of that trade. Snapshots must therefore hold
// the `File` objects themselves rather than rebuild them per call.
const fileIdentities = new WeakMap<File, number>();
let nextFileIdentity = 0;

function fileIdentity(file: File): number {
    let identity = fileIdentities.get(file);

    if (identity === undefined) {
        nextFileIdentity += 1;
        identity = nextFileIdentity;
        fileIdentities.set(file, identity);
    }

    return identity;
}

function serialise(value: unknown): string {
    return JSON.stringify(value, (_key, current) => {
        if (typeof File !== 'undefined' && current instanceof File) {
            return `${current.name}|${current.size}|${current.lastModified}|#${fileIdentity(current)}`;
        }

        return current === undefined ? null : current;
    });
}

/**
 * Declares a form's unsaved-changes check to the retaining modal around it (CU-86d3wvwq7).
 *
 * Dirty is a snapshot comparison rather than vee-validate's `meta.dirty`, which does not see
 * `useFieldArray` edits and cannot see state kept outside the form (an editor body, attachments).
 * Pass a getter for exactly the state the operator can change; forms that seed fields after mount
 * call `markClean()` once the seed has settled so an untouched form never warns.
 */
export function useModalUnsavedChanges(getSnapshot: () => unknown, { auto = true }: ModalUnsavedChangesOptions = {}) {
    const registry = inject(RETAINED_MODAL_DIRTY_KEY, null);
    let baseline: string | null = null;

    function markClean(): void {
        baseline = serialise(getSnapshot());
    }

    function isDirty(): boolean {
        return baseline !== null && serialise(getSnapshot()) !== baseline;
    }

    let unregister: (() => void) | undefined;

    // Registered on mount, not in setup: the unregister runs in `onBeforeUnmount`, so a setup that
    // never mounts (an aborted `<Suspense>` branch, a `v-if` that flips within the tick) would
    // otherwise leave a check registered forever and the modal would report a phantom form dirty.
    onMounted(() => {
        unregister = registry?.register(isDirty);

        if (auto) {
            nextTick(markClean);
        }
    });

    onBeforeUnmount(() => {
        unregister?.();
        unregister = undefined;
    });

    return { isDirty, markClean };
}
